import React from 'react';
import * as LucideIcons from 'lucide-react';
import { solutions, sectorImages } from '../data/mockData';
import { useScrollReveal } from '../hooks/useScrollReveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const SolutionsSection = () => {
  const [headerRef, isHeaderVisible] = useScrollReveal();
  const [gridRef, isGridVisible] = useScrollReveal();
  const [imagesRef, isImagesVisible] = useScrollReveal();

  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent size={24} /> : null;
  };

  return (
    <section id="solutions">
      {/* Cyan Header Band */}
      <div className="bg-[#00BCE4] py-20 lg:py-28 relative overflow-hidden">
        {/* Jumping mascot */}
        <div className="absolute right-8 lg:right-16 -bottom-2 hidden md:block" style={{ animation: 'mascot-float 3s ease-in-out infinite 0.5s' }}>
          <img
            src="/immi-jumping-nobg.webp"
            alt="Immi mascot jumping"
            className="w-40 lg:w-56 h-auto"
            style={{ filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))', mixBlendMode: 'screen' }}
          />
        </div>
        <div
          ref={headerRef}
          className={`section-container text-center reveal ${isHeaderVisible ? 'visible' : ''}`}
        >
          <p
            className="text-sm md:text-base font-semibold text-white/80 uppercase tracking-[2px] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Whether it's an out-of-the-box system, or a bespoke solution, we know we can help!
          </p>
          <h2
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Find your solution and ask<br className="hidden md:block" /> about integrations...
          </h2>
        </div>
      </div>

      {/* Solutions Accordion Grid */}
      <div className="bg-white py-20 lg:py-28">
        <div
          ref={gridRef}
          className={`section-container reveal ${isGridVisible ? 'visible' : ''}`}
        >
          <Accordion type="single" collapsible className="grid md:grid-cols-2 gap-4">
            {solutions.map((solution, index) => (
              <AccordionItem
                key={solution.title}
                value={`solution-${index}`}
                className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#00BCE4]/40 transition-all duration-300 data-[state=open]:border-[#00BCE4] data-[state=open]:shadow-xl data-[state=open]:shadow-[#00BCE4]/10"
              >
                <AccordionTrigger className="px-6 py-5 hover:no-underline group">
                  <div className="flex items-center gap-4 text-left">
                    <div className="w-12 h-12 rounded-lg bg-[#00BCE4]/10 flex items-center justify-center text-[#00BCE4] flex-shrink-0 group-hover:bg-[#00BCE4] group-hover:text-white transition-all duration-300">
                      {getIcon(solution.icon)}
                    </div>
                    <h3
                      className="text-base font-semibold text-gray-900 group-hover:text-[#00BCE4] transition-colors duration-300"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {solution.title}
                    </h3>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5">
                  <p
                    className="text-sm text-gray-600 leading-relaxed pl-16"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {solution.description}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Sector Images Grid */}
      <div
        ref={imagesRef}
        className={`reveal ${isImagesVisible ? 'visible' : ''}`}
      >
        <div className="grid grid-cols-2 lg:grid-cols-4">
          {sectorImages.map((image) => (
            <div key={image.alt} className="relative group overflow-hidden aspect-[5/4]">
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500"></div>
              <div className="absolute inset-0 flex items-end p-6">
                <span
                  className="text-white font-semibold text-lg opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {image.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
